/**
 * React Query hook + helpers for Reports > On Order.
 *
 *   useOnOrder(filters, sort, enabled)  GET /v1/retail/on-order
 *   downloadOnOrderXlsx(filters, sort)  GET /v1/retail/on-order/export
 *
 * Every line on an open purchase order, with what has not arrived yet. The api
 * filters and sorts, and cuts a very long list to its first 5000 rows (the totals
 * and `total_rows` always cover every match, and the Excel export carries every
 * line), so the page renders the rows as given and sends the sort to the api.
 *
 * Contract:
 *
 *   GET /v1/retail/on-order
 *     ?store_no=...      optional, repeatable (a filter, never a scope)
 *     &vendor=...        optional, repeatable vendor number
 *     &status=...        optional, repeatable PO status code (O is Open)
 *     &q=...             optional text for the item number, description or PO number
 *     &past_cancel=true  optional, only lines past their cancel date
 *     &sort=...&dir=...  optional column and direction; default is the soonest cancel date
 *   -> { as_of, scope, today, summary, rows, total_rows, truncated, locations, vendors, statuses }
 *
 * The row types come from `@ampersandbrands/ui`, because the shared screen owns the
 * wire shape. The daily email goes through the generic `useReportSubscriptions`
 * hook; what lives here is the pure pair that decides which filters a subscription
 * saves. Same 404/503 "not deployed yet" convention as the other report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type {
  OnOrderFilters,
  OnOrderOption,
  OnOrderRow,
  OnOrderSort,
  OnOrderSortColumn,
  OnOrderStoreOption,
  OnOrderSummary,
} from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const ON_ORDER_API_BASE = `${RETAIL_API_BASE}/on-order`;

export const EMPTY_ON_ORDER_FILTERS: OnOrderFilters = {
  store_no: [],
  vendor: [],
  status: [],
  q: '',
  past_cancel: false,
};

/** The default order: soonest cancel date first (the api's own default). */
export const DEFAULT_ON_ORDER_SORT: OnOrderSort = { col: null, dir: 'asc' };

const SORT_COLUMNS: readonly OnOrderSortColumn[] = [
  'store_no',
  'ship_name',
  'vendor_name',
  'po_no',
  'status',
  'statement',
  'item_no',
  'descr',
  'ord_qty',
  'rec_qty',
  'open_cost',
  'ord_date',
  'deliv_date',
  'cancel_date',
];

export type OnOrderResponse = {
  as_of: string | null;
  scope: 'fleet' | 'store';
  today: string;
  summary: OnOrderSummary;
  rows: OnOrderRow[];
  total_rows: number;
  truncated: boolean;
  locations: OnOrderStoreOption[];
  vendors: OnOrderOption[];
  statuses: OnOrderOption[];
};

/** Query string for the report and its export. Lists repeat their key. */
export function buildOnOrderQs(filters: OnOrderFilters, sort: OnOrderSort): string {
  const sp = new URLSearchParams();
  for (const s of filters.store_no) sp.append('store_no', s);
  for (const v of filters.vendor) sp.append('vendor', v);
  for (const s of filters.status) sp.append('status', s);
  if (filters.q) sp.set('q', filters.q);
  if (filters.past_cancel) sp.set('past_cancel', 'true');
  if (sort.col) {
    sp.set('sort', sort.col);
    sp.set('dir', sort.dir);
  }
  return sp.toString();
}

/** Read the filters back out of the page's URL. */
export function parseOnOrderFilters(sp: URLSearchParams): OnOrderFilters {
  return {
    store_no: sp.getAll('store_no').filter(Boolean),
    vendor: sp.getAll('vendor').filter(Boolean),
    status: sp.getAll('status').filter(Boolean),
    q: (sp.get('q') ?? '').trim(),
    past_cancel: sp.get('past_cancel') === '1' || sp.get('past_cancel') === 'true',
  };
}

/** Read the sort back out of the page's URL; an unknown column means the default order. */
export function parseOnOrderSort(sp: URLSearchParams): OnOrderSort {
  const col = sp.get('sort');
  if (!col || !(SORT_COLUMNS as readonly string[]).includes(col)) return DEFAULT_ON_ORDER_SORT;
  return { col: col as OnOrderSortColumn, dir: sp.get('dir') === 'desc' ? 'desc' : 'asc' };
}

/** Write the filters into the page's URL, leaving defaults out. */
export function writeOnOrderFilters(sp: URLSearchParams, filters: OnOrderFilters): void {
  sp.delete('store_no');
  sp.delete('vendor');
  sp.delete('status');
  sp.delete('q');
  sp.delete('past_cancel');
  for (const s of filters.store_no) sp.append('store_no', s);
  for (const v of filters.vendor) sp.append('vendor', v);
  for (const s of filters.status) sp.append('status', s);
  if (filters.q) sp.set('q', filters.q);
  if (filters.past_cancel) sp.set('past_cancel', '1');
}

/** Write the sort into the page's URL, leaving the default order out. */
export function writeOnOrderSort(sp: URLSearchParams, sort: OnOrderSort): void {
  sp.delete('sort');
  sp.delete('dir');
  if (sort.col) {
    sp.set('sort', sort.col);
    sp.set('dir', sort.dir);
  }
}

/**
 * What a subscription saves. The lists are joined into comma strings because the
 * api keeps scalar filter values only, and the past-cancel switch is saved only when
 * on, since an unchecked box is not a filter. The sort is never saved: the emailed
 * workbook always uses the default order.
 */
export function onOrderSubscriptionFilters(f: OnOrderFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.store_no.length > 0) out.store_no = f.store_no.join(',');
  if (f.vendor.length > 0) out.vendor = f.vendor.join(',');
  if (f.status.length > 0) out.status = f.status.join(',');
  if (f.q) out.q = f.q;
  if (f.past_cancel) out.past_cancel = true;
  return out;
}

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

const ZERO_SUMMARY: OnOrderSummary = {
  open_pos: 0,
  lines: 0,
  ordered_units: 0,
  open_units: 0,
  open_cost: 0,
  past_cancel_lines: 0,
};

/** Tolerant read, so a partial or absent payload degrades to an empty report rather than a crash. */
export function normalizeOnOrder(raw: unknown): OnOrderResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rows = arr<OnOrderRow>(r.rows);
  const summary = { ...ZERO_SUMMARY, ...((r.summary ?? {}) as Partial<OnOrderSummary>) };
  return {
    as_of: typeof r.as_of === 'string' ? r.as_of : null,
    scope: r.scope === 'store' ? 'store' : 'fleet',
    today: typeof r.today === 'string' ? r.today : '',
    summary,
    rows,
    total_rows: typeof r.total_rows === 'number' ? r.total_rows : rows.length,
    truncated: r.truncated === true,
    locations: arr<OnOrderStoreOption>(r.locations),
    vendors: arr<OnOrderOption>(r.vendors),
    statuses: arr<OnOrderOption>(r.statuses),
  };
}

export function useOnOrder(filters: OnOrderFilters, sort: OnOrderSort, enabled: boolean) {
  const query = useQuery<OnOrderResponse, Error>({
    queryKey: ['retail', 'on-order', filters, sort],
    queryFn: async () => {
      const qs = buildOnOrderQs(filters, sort);
      return normalizeOnOrder(await apiGet<unknown>(qs ? `${ON_ORDER_API_BASE}?${qs}` : ON_ORDER_API_BASE));
    },
    // The mirror refreshes every 5 minutes; a minute of cache is plenty. Keep the old
    // rows on screen while a new sort or filter loads, so the table does not flash empty.
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const data = query.data;
  return {
    asOf: data?.as_of ?? null,
    scope: data?.scope ?? 'fleet',
    summary: data?.summary ?? ZERO_SUMMARY,
    rows: data?.rows ?? [],
    totalRows: data?.total_rows ?? 0,
    locations: data?.locations ?? [],
    vendors: data?.vendors ?? [],
    statuses: data?.statuses ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Browser download of the Excel export, following the filters and sort on screen. */
export async function downloadOnOrderXlsx(filters: OnOrderFilters, sort: OnOrderSort): Promise<void> {
  const qs = buildOnOrderQs(filters, sort);
  const res = await fetch(`${ON_ORDER_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'on-order.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
